import React from "react"
import { Divider, Grid, Typography } from "@material-ui/core"
import { DoneAll as DoneAllIcon, LocalOffer as TagIcon } from "@material-ui/icons"

import { TagOption } from "@/protocols/chatBotConstructor"

import { convertRGBAObjectToString } from "@/utils/color"

import SelectInput from "@/pages/Admin/Flow/FlowConstructor/FlowEditor/components/SelectInput"

import useStyles from "@/pages/Admin/Flow/FlowConstructor/FlowEditor/resources/Blocks/AIManageClientTagsBlock/TagMultiSelect/styles"

const SELECT_ALL_OPTION_ID = -1

const selectAllOption: TagOption = {
	id: SELECT_ALL_OPTION_ID,
	name: "Incluir todas as tags",
	color: { r: 0, g: 0, b: 0 }
}

type TagMultiSelectProps = {
	value: TagOption[]
	onChange: (tags: TagOption[] | null) => void
	options: TagOption[]
	placeholder?: string
	getOptionSelected?: (option: TagOption, value: TagOption) => boolean
	noOptionsText?: string
	loadingText?: string
	renderTag?: (option: TagOption) => React.ReactNode
	onUpdate?: () => Promise<void>
}

const TagMultiSelect: React.FC<TagMultiSelectProps> = (props) => {
	const {
		value,
		onChange,
		options,
		placeholder,
		getOptionSelected,
		noOptionsText,
		loadingText,
		renderTag,
		onUpdate
	} = props

	const classes = useStyles()

	const isOptionSelected = (option: TagOption): boolean => {
		const selectedValues = value ?? []

		if (getOptionSelected) {
			return selectedValues.some(selected => getOptionSelected(option, selected))
		}

		return selectedValues.some(selected => option.name === selected.name)
	}

	const isEveryOptionSelected = options.length > 0 && options.every(isOptionSelected)

	const selectableOptions = isEveryOptionSelected ? options : [selectAllOption, ...options]

	const handleChange = (selected: TagOption[] | null) => {
		const hasSelectedAllOption = selected?.some(option => option.id === SELECT_ALL_OPTION_ID)

		onChange(hasSelectedAllOption ? options : selected)
	}

	const overrideGetOptionSelected = (option: TagOption, selectedValue: TagOption): boolean => {
		if (option.id === SELECT_ALL_OPTION_ID) {
			return false
		}

		if (getOptionSelected) {
			return getOptionSelected(option, selectedValue)
		}

		return option.name === selectedValue.name
	}

	const overrideRenderOption = (option: TagOption): React.ReactNode => {
		if (option.id === SELECT_ALL_OPTION_ID) {
			return (
				<Grid
					container
					direction="column"
					className={classes.selectAllOption}
				>
					<Grid item>
						<Grid
							container
							alignItems="center"
							className={classes.selectAllOptionRow}
						>
							<Grid item>
								<DoneAllIcon className={classes.selectAllOptionIcon} />
							</Grid>

							<Grid item>
								<strong className={classes.selectAllOptionLabel}>
									{option.name}
								</strong>
							</Grid>
						</Grid>
					</Grid>

					<Grid item>
						<Divider className={classes.selectAllOptionDivider} />
					</Grid>
				</Grid>
			)
		}

		return (
			<Grid
				container
				alignItems="center"
				className={classes.tagOption}
			>
				<Grid item>
					<TagIcon
						className={classes.tagColor}
						style={{ color: convertRGBAObjectToString(option.color) }}
					/>
				</Grid>

				<Grid item>
					<Typography className={classes.tagLabel}>
						{option.name}
					</Typography>
				</Grid>
			</Grid>
		)
	}

	return (
		<SelectInput
			multiple
			value={value}
			onChange={handleChange}
			options={selectableOptions}
			placeholder={placeholder}
			getOptionSelected={overrideGetOptionSelected}
			noOptionsText={noOptionsText}
			loadingText={loadingText}
			renderTag={renderTag}
			renderOption={overrideRenderOption}
			onUpdate={onUpdate}
		/>
	)
}

export default TagMultiSelect
